import { h } from 'preact';
import { useEffect } from 'preact/hooks';

import { Checkbox, CheckboxProps } from './Checkbox';
import { MoreIcon } from './Icons';
import { OrderSummaryItem } from './OrderSummaryItem';
import { PaymentButtons } from './PaymentButtons';
import { RadioOption } from './Radio';

export interface OrderSummaryFooterProps {
    paymentOptions: RadioOption[];
    additionalPaymentOptions?: RadioOption[];
    onSubmit: (paymentMethod: RadioOption) => void;
    onSubmitAdditional: (paymentMethod: RadioOption) => void;
    isDropdownExpanded: boolean;
    onDropdownButtonClick: () => void;
    onDropdownOutsideClick: () => void;
    isSubmitting: boolean;
    printReceiptTooltip?: string;
    print?: CheckboxProps;
    printTooltip?: string;
    printReceipt?: CheckboxProps;
    partialPayments?: {
        text: string;
        amount: string;
        onRemove: () => void;
    }[];
    totalSum?: {
        text: string;
        amount: string;
    };
    remainingSum?: {
        text: string;
        amount: string;
    };
    // With items in the basket and nothing to pay, one button completes the sale in place of the payment buttons.
    completeSale?: {
        label: string;
        onClick: () => void;
    };
}

export const OrderSummaryFooter = (props: OrderSummaryFooterProps): JSX.Element => {
    const onOutsideClick = () => {
        props.onDropdownOutsideClick();
    };

    useEffect(() => {
        document.addEventListener('click', onOutsideClick);

        return () => {
            document.removeEventListener('click', onOutsideClick);
        };
    }, []);

    return (
        <div class="pt-4 pb-6 bg-gray-100 border-t xl:sticky xl:bottom-0 xl:bg-gradient-to-t xl:from-neutral-100 xl:from-70% xl:via-neutral-100 xl:to-transparent order-summary__confirm-button-container">
            {props.totalSum && (
                <div class="flex justify-between gap-2 pb-4 mb-4 text-base font-bold border-b">
                    <div class="text-sm">{props.totalSum.text}</div>
                    <div>{props.totalSum.amount}</div>
                </div>
            )}
            {props.partialPayments?.length > 0 && (
                <div class="border-b pb-2 mb-4">
                    {props.partialPayments.map(({ text, amount, onRemove }) => (
                        <OrderSummaryItem name={text} onRemove={onRemove} price={amount} />
                    ))}
                </div>
            )}
            {props.remainingSum && (
                <div class="flex justify-between gap-2 pb-4 text-lg leading-8 font-bold">
                    <div>{props.remainingSum.text}</div>
                    <div class="h3">{props.remainingSum.amount}</div>
                </div>
            )}
            {props.print && (
                <div class="flex gap-2 mt-2">
                    {props.print && (
                        <div class="relative">
                            {props.printTooltip && (
                                <div class="absolute bg-white min-w-[128px] rounded-base shadow-button bottom-full mb-3 py-3 px-4 text-center left-0 right-0 after:absolute after:bg-white after:rotate-45 after:translate-y-1/2 after:left-0 after:right-0 after:mx-auto after:bottom-0 after:w-4 after:h-4">
                                    {props.printTooltip}
                                </div>
                            )}
                            <Checkbox class="basis-1/2" {...props.print} />
                        </div>
                    )}
                    {props.printReceipt && (
                        <div class="relative">
                            {props.printReceiptTooltip && (
                                <div class="absolute bg-white min-w-[128px] rounded-base shadow-button bottom-full mb-3 py-3 px-4 text-center left-0 right-0 after:absolute after:bg-white after:rotate-45 after:translate-y-1/2 after:left-0 after:right-0 after:mx-auto after:bottom-0 after:w-4 after:h-4">
                                    {props.printReceiptTooltip}
                                </div>
                            )}
                            <Checkbox class="basis-1/2" {...props.printReceipt} />
                        </div>
                    )}
                </div>
            )}
            <div class="flex gap-2 mt-4">
                {props.completeSale ? (
                    <div class="flex grow min-w-0 flex-col gap-2">
                        <button
                            className="btn btn-light-blue h-[60px] min-w-0 px-3"
                            onClick={props.completeSale.onClick}
                            disabled={props.isSubmitting}
                        >
                            {props.completeSale.label}
                        </button>
                        {props.paymentOptions.length > 0 && (
                            <PaymentButtons
                                options={props.paymentOptions}
                                onSubmit={props.onSubmit}
                                disabled={props.isSubmitting}
                            />
                        )}
                    </div>
                ) : (
                    <PaymentButtons
                        options={props.paymentOptions}
                        onSubmit={props.onSubmit}
                        disabled={props.isSubmitting}
                    />
                )}
                {props.additionalPaymentOptions && (
                    <div class="relative">
                        <button
                            class="btn btn-border px-2 h-full"
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onDropdownButtonClick();
                            }}
                        >
                            <MoreIcon class="w-6 h-6" />
                        </button>
                        {props.isDropdownExpanded && (
                            <ul class="absolute bottom-full right-0 rounded-base bg-white shadow-dropdown mb-2 overflow-hidden">
                                {props.additionalPaymentOptions.map((paymentMethod: RadioOption) => (
                                    <li className="flex">
                                        <button
                                            className="px-4 py-2.5 whitespace-nowrap text-left font-normal hover:bg-neutral-100 transition-colors cursor-pointer w-full"
                                            onClick={() => props.onSubmitAdditional(paymentMethod)}
                                            disabled={props.isSubmitting}
                                        >
                                            {paymentMethod.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
